import { useQuery } from "@tanstack/react-query";
import { use } from "react";
import { useAuthentication } from "../../auth/useAuthentication";
import { UnauthenticatedError } from "../../errors/unauthenticatedError.ts";
import { InternationalizationContext } from "../../i18n/InternationalizationContext";
import { frontpageContentSchema } from "./frontpageContent";

// Keep this copy in authenticated static assets; importing it here would expose it
// in public JavaScript and source maps. An authenticated API can replace this later.
export const useFrontpageContent = () => {
  const { language } = use(InternationalizationContext);
  const { getToken, isAuthenticated } = useAuthentication();
  return useQuery({
    queryKey: ["confidentialContent", "frontpage-content", language],
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new UnauthenticatedError("No access token");
      const response = await fetch(
        `/confidential/frontpage-content-${language.toLowerCase()}.json`,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.status === 401)
        throw new UnauthenticatedError("Access token rejected");
      if (!response.ok)
        throw new Error(
          `Loading the front page content failed: ${response.status}`,
        );
      const content: unknown = await response.json();
      return frontpageContentSchema.parse(content);
    },
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
  });
};
